/**
 * The three.js side of the app: terrain, props, structures, build ghosts,
 * villagers, sky rig, orbit camera and picking. `hello` builds the world once;
 * every `tick` applies a `WorldDiff` incrementally — nothing is rebuilt.
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { HelloMessage, TickMessage, VillageEvent, Villager } from '@village/shared';
import { Terrain, disposeGroup } from './terrain';
import { PropLayer } from './props';
import { GhostLayer, StructureLayer, structureOf } from './structures';
import { VillagerLayer } from './villagers';
import { Effects } from './effects';
import { SkyRig } from './sky';
import { PEEK_VISIBLE } from '../ui/sheet';

export interface Picked {
  villagerId?: string;
  structureId?: string;
}

/** `--sa-b` resolves env(safe-area-inset-bottom) for us; it is 0 on a desktop. */
function safeBottom(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--sa-b');
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

export class VillageScene {
  readonly scene = new THREE.Scene();
  private renderer: THREE.WebGLRenderer;
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private sky: SkyRig | null = null;
  private terrain: Terrain | null = null;
  private props: PropLayer | null = null;
  private structures: StructureLayer | null = null;
  private ghosts: GhostLayer | null = null;
  private villagers: VillagerLayer | null = null;
  private effects = new Effects();
  private colors = new Map<string, string>();
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private down: { x: number; y: number; t: number } | null = null;
  private last = performance.now();
  private running = false;
  private loaded: { seed: number; size: number } | null = null;
  private figure = document.getElementById('stage-figure');
  private frameEl = document.getElementById('stage-frame');
  private side = 0;

  onSelect: (picked: Picked | null) => void = () => {};

  /** Side of the square, in CSS pixels. */
  get squareSide(): number {
    return this.side;
  }

  constructor(private canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 400);
    this.camera.position.set(18, 16, 24);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.075;
    this.controls.rotateSpeed = 0.7;
    this.controls.zoomSpeed = 0.9;
    this.controls.panSpeed = 0.7;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 6;
    this.controls.maxDistance = 90;
    this.controls.minPolarAngle = 0.18;
    this.controls.maxPolarAngle = Math.PI / 2 - 0.08;
    this.controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    this.controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };

    window.addEventListener('resize', this.resize);
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointerup', this.onPointerUp);
    this.resize();
  }

  // ------------------------------------------------------------- world

  setWorld(hello: HelloMessage): void {
    const { world } = hello;
    // the server resends `hello` when the active brain flips; if it is the same
    // world, keep the camera where the viewer put it
    const same = this.loaded?.seed === world.seed && this.loaded?.size === world.size;
    const keepPosition = this.camera.position.clone();
    const keepTarget = this.controls.target.clone();
    this.clearWorld();
    this.colors = new Map(hello.villagers.map((v) => [v.id, v.color]));

    this.terrain = new Terrain(world);
    this.scene.add(this.terrain.group);

    this.props = new PropLayer(this.terrain);
    this.props.sync(world.props);
    this.scene.add(this.props.group);

    this.structures = new StructureLayer(this.terrain, (id) => this.colors.get(id) ?? '#FFD6C2');
    this.structures.sync(world.structures);
    this.scene.add(this.structures.group);

    this.ghosts = new GhostLayer(this.terrain);
    this.ghosts.sync(hello.villagers);
    this.scene.add(this.ghosts.group);

    this.villagers = new VillagerLayer(this.terrain, hello.tickMs);
    this.villagers.sync(hello.villagers);
    this.scene.add(this.villagers.group);

    this.scene.add(this.effects.group);

    this.sky = new SkyRig(this.scene, world.size);
    this.sky.setClock(hello.clock);

    if (same) {
      this.camera.position.copy(keepPosition);
      this.controls.target.copy(keepTarget);
      this.controls.update();
    } else {
      this.frame(world.size);
    }
    this.loaded = { seed: world.seed, size: world.size };
    if (!this.running) {
      this.running = true;
      requestAnimationFrame(this.tickFrame);
    }
  }

  private clearWorld(): void {
    this.scene.remove(this.effects.group);
    for (const child of [...this.scene.children]) {
      this.scene.remove(child);
      disposeGroup(child);
    }
    this.terrain?.dispose();
    this.terrain = null;
    this.props = null;
    this.structures = null;
    this.ghosts = null;
    this.villagers = null;
    this.sky = null;
  }

  onTick(tick: TickMessage): void {
    const terrain = this.terrain;
    if (!terrain) return;

    const diff = tick.worldDiff;
    if (diff) {
      for (const [cell, height, tile] of diff.cells ?? []) terrain.setCell(cell, height, tile);
      terrain.flush();
      if (diff.cells?.length) this.props?.reseat();
      for (const id of diff.propsRemoved ?? []) this.props?.remove(id);
      for (const prop of diff.propsAdded ?? []) this.props?.add(prop);
      for (const id of diff.structuresRemoved ?? []) this.structures?.remove(id);
      for (const s of diff.structuresAdded ?? []) this.structures?.add(s);
    }

    for (const v of tick.villagers) this.colors.set(v.id, v.color);
    this.ghosts?.sync(tick.villagers);
    this.villagers?.sync(tick.villagers);
    this.sky?.setClock(tick.clock);
    this.popEvents(tick.events, tick.villagers);
  }

  /** A small puff wherever an event says something happened. */
  private popEvents(events: VillageEvent[], villagers: Villager[]): void {
    if (!this.terrain || events.length === 0) return;
    const byId = new Map(villagers.map((v) => [v.id, v.color] as const));
    for (const e of events) {
      if (e.x === undefined || e.z === undefined) continue;
      const color = byId.get(e.villagerId) ?? this.colors.get(e.villagerId) ?? '#FFFFFF';
      this.effects.pop(e.x, this.terrain.surfaceY(e.x, e.z), e.z, color);
    }
  }

  select(id: string | null): void {
    this.villagers?.setSelected(id);
  }

  /** Ease the camera toward a villager without changing the zoom level. */
  lookAt(v: Villager): void {
    if (!this.terrain) return;
    const y = this.terrain.surfaceY(v.x, v.z);
    const target = new THREE.Vector3(v.x, y + 0.5, v.z);
    const offset = this.camera.position.clone().sub(this.controls.target);
    this.controls.target.copy(target);
    this.camera.position.copy(target).add(offset);
    this.controls.update();
  }

  /** The square is orientation-independent, so one framing serves every screen. */
  private frame(size: number): void {
    const c = (size - 1) / 2;
    this.controls.target.set(c, 0.55, c);
    const d = size * 1.42;
    this.camera.position.set(c + d * 0.82, d * 0.86, c + d * 0.82);
    this.controls.maxDistance = size * 3.4;
    this.controls.minDistance = Math.max(4, size * 0.22);
    this.controls.update();
  }

  // ------------------------------------------------------------- picking

  private onPointerDown = (ev: PointerEvent) => {
    this.down = { x: ev.clientX, y: ev.clientY, t: performance.now() };
  };

  private onPointerUp = (ev: PointerEvent) => {
    const d = this.down;
    this.down = null;
    if (!d) return;
    const moved = Math.hypot(ev.clientX - d.x, ev.clientY - d.y);
    if (moved > 9 || performance.now() - d.t > 600) return; // a drag, not a tap

    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const targets: THREE.Object3D[] = [];
    if (this.villagers) targets.push(this.villagers.group);
    if (this.structures) targets.push(this.structures.group);
    const hits = this.raycaster.intersectObjects(targets, true);
    for (const hit of hits) {
      if (hit.object.type === 'Sprite') continue;
      const villagerId = this.ownerChain(hit.object);
      if (villagerId) {
        this.onSelect({ villagerId });
        return;
      }
      const s = structureOf(hit.object);
      if (s) {
        this.onSelect({ structureId: s.userData.structureId as string, villagerId: s.userData.builtBy as string | undefined });
        return;
      }
    }
    this.onSelect(null);
  };

  private ownerChain(obj: THREE.Object3D): string | undefined {
    let cur: THREE.Object3D | null = obj;
    while (cur) {
      const id = cur.userData?.villagerId as string | undefined;
      if (id) return id;
      cur = cur.parent;
    }
    return undefined;
  }

  // ------------------------------------------------------------- loop

  /**
   * Lay the square out in whatever the UI leaves over: left of the panel on
   * desktop, between the top bar and the sheet's peek line on a phone.
   */
  private resize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const wide = w > 720;
    const topbar = document.getElementById('topbar');
    const panel = document.getElementById('panel');
    const top = (topbar?.getBoundingClientRect().bottom ?? 60) + (wide ? 12 : 10);
    const caption = 26; // room for the label under the frame

    let left: number;
    let availW: number;
    let availH: number;
    if (wide) {
      const panelLeft = panel?.getBoundingClientRect().left ?? w - 384;
      left = 12;
      availW = Math.max(120, panelLeft - 24);
      availH = Math.max(120, h - top - 12 - caption);
    } else {
      left = 16;
      availW = Math.max(120, w - 32);
      availH = Math.max(120, h - top - PEEK_VISIBLE - safeBottom() - 10 - caption);
    }

    const side = Math.max(120, Math.round(Math.min(availW, availH)));
    this.side = side;

    if (this.figure) {
      // centred in the leftover space on desktop; pinned under the top bar on a
      // phone, so the sheet can rise over its lower edge without hiding it
      const slack = wide ? (availH - side) / 2 : 0;
      this.figure.style.left = `${Math.round(left + (availW - side) / 2)}px`;
      this.figure.style.top = `${Math.round(top + slack)}px`;
      this.figure.style.width = `${side}px`;
    }
    if (this.frameEl) {
      this.frameEl.style.width = `${side}px`;
      this.frameEl.style.height = `${side}px`;
    }

    this.renderer.setSize(side, side, false);
    this.camera.aspect = 1;
    this.camera.updateProjectionMatrix();
  };

  private tickFrame = (now: number) => {
    if (!this.running) return;
    requestAnimationFrame(this.tickFrame);
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;

    this.controls.update();
    this.sky?.update(dt);
    this.villagers?.update(now);
    this.effects.update(dt);

    if (this.structures) {
      const t = now / 1000;
      for (const fire of this.structures.fires) {
        const flicker = 0.75 + Math.sin(t * 9 + fire.phase) * 0.12 + Math.sin(t * 23 + fire.phase * 2) * 0.08;
        fire.light.intensity = 5.5 * flicker;
        fire.flame.scale.set(0.9 + flicker * 0.18, 0.85 + flicker * 0.3, 0.9 + flicker * 0.18);
        fire.flame.rotation.y = t * 1.6;
      }
    }

    this.renderer.render(this.scene, this.camera);
  };
}
